<script setup lang="ts">
    /**
     * 📘docs: https://onewoom.atlassian.net/wiki/spaces/ECOM/pages/944308228
     */
    const props = defineProps<IWidgetProps<ReviewsUGCWidgetData>>();

    const { data, cmsComponentProps } = useWidget(props);
    const { language } = useLocales();
    const route = useRoute();

    const widgetRef = ref<HTMLDivElement | null>(null);
    const isLoading = ref(true);
    const scriptKey = ref(Date.now());
    const isWidgetLoaded = ref(false);

    useHead(() => ({
        script: [
            {
                src: 'https://widget.reviews.io/modern-widgets/ugc.js',
                key: `reviews-ugc-script-${scriptKey.value}`,
                async: true,
                onload: () => {
                    const checkContent = () => {
                        if (widgetRef.value?.querySelector('.MediaGalleryWidget .MediaGalleryWidget__inner')) {
                            isLoading.value = false;
                            if (observer) observer.disconnect();
                        }
                    };

                    const observer = new MutationObserver(() => {
                        checkContent();
                    });

                    if (widgetRef.value) {
                        observer.observe(widgetRef.value, {
                            childList: true,
                            subtree: true,
                        });
                        checkContent();
                    }

                    // Fallback timeout in case widget doesn't render anything
                    setTimeout(() => {
                        isLoading.value = false;
                        if (observer) observer.disconnect();
                    }, 5000);
                },
                onerror: (error: Event) => {
                    console.error(`Failed to load ugc script: ${error}`);
                    isLoading.value = false;
                },
            },
        ],
    }));

    const loadAndInitReviewsWidget = async (): Promise<void> => {
        if (isWidgetLoaded.value) return;
        isWidgetLoaded.value = true;

        try {
            if (!data.reviews_widget_id) {
                isLoading.value = false;
                return;
            }

            await nextTick();

            if (!widgetRef.value) {
                isLoading.value = false;
                return;
            }

            widgetRef.value.innerHTML = '';

            const widgetDiv = document.createElement('div');
            widgetDiv.className = 'reviews-io-ugc-widget';
            widgetDiv.setAttribute('widget-id', `${data.reviews_widget_id}`);
            widgetDiv.setAttribute('data-store-name', 'woom.com');
            widgetDiv.setAttribute('lang', `${language.value}`);

            widgetRef.value.appendChild(widgetDiv);

            // Update this *after* appending widgetDiv to trigger script reload
            scriptKey.value = Date.now();
        } finally {
            isWidgetLoaded.value = false;
        }
    };

    const waitForWidgetRef = async () => {
        let retries = 10;

        while (!widgetRef.value && retries > 0) {
            await nextTick();
            await new Promise((resolve) => setTimeout(resolve, 100));
            retries--;
        }

        if (widgetRef.value) {
            loadAndInitReviewsWidget();
        } else {
            console.warn('widgetRef never became available.');
        }
    };

    onMounted(() => {
        waitForWidgetRef();
    });

    watch([() => route.path, () => widgetRef.value], () => {
        if (data.reviews_widget_id) {
            isLoading.value = true;
            loadAndInitReviewsWidget();
        }
    });
</script>

<template>
    <lazy-atm-container
        v-if="data.reviews_widget_id"
        class="mb-10 md:mb-12"
        v-bind="cmsComponentProps">
        <div class="relative lg:min-h-96">
            <lazy-atm-loader-circular
                aria-busy="true"
                v-if="isLoading"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <client-only>
                <div
                    ref="widgetRef"
                    v-show="!isLoading"></div>
            </client-only>
        </div>
    </lazy-atm-container>
</template>

<style scoped>
    @reference "#main.css";
    :deep(.MediaGalleryWidget-prefix .MediaGalleryWidget) {
        .MediaGalleryWidget__list-container {
            @apply relative flex;

            .MediaGalleryWidget__scrollButton {
                @apply absolute top-auto bottom-0 z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-transparent bg-center bg-no-repeat outline-offset-0 transition-all duration-200 before:absolute before:h-12 before:w-12 before:rounded-full before:bg-white after:absolute not-disabled:hover:before:animate-pop-sm not-disabled:hover:after:animate-pop not-disabled:active:before:scale-90 disabled:after:opacity-60 lg:h-10 lg:w-10 lg:before:h-10 lg:before:w-10;
                &:hover {
                    /* reset default style */
                    transform: unset;
                }
                &:after {
                    @apply h-6 w-6;
                    background-image: url('data:image/svg+xml,%3Csvg%20fill%3D%22%23000%22%20viewBox%3D%220%200%2024%2024%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M12.612%204.0103C12.7242%204.03027%2012.8757%204.09845%2012.9638%204.16863C13.0174%204.21135%2014.5953%205.87976%2016.4702%207.87618C19.7379%2011.3556%2019.8812%2011.5109%2019.9268%2011.6224C20.0244%2011.8608%2020.0244%2012.1428%2019.9268%2012.3812C19.8812%2012.4927%2019.738%2012.6478%2016.4533%2016.1441C14.569%2018.1496%2012.9892%2019.8185%2012.9425%2019.8527C12.6402%2020.0743%2012.2045%2020.0428%2011.9206%2019.7787C11.6313%2019.5096%2011.5496%2019.0567%2011.7233%2018.6851C11.7593%2018.6082%2012.2638%2018.0625%2014.4401%2015.7469L17.1126%2012.9031L10.9251%2012.9021C5.13249%2012.9011%204.73032%2012.8991%204.62588%2012.8701C4.26897%2012.771%204%2012.3979%204%2012.0018C4%2011.6057%204.26897%2011.2326%204.62588%2011.1335C4.73032%2011.1045%205.13249%2011.1025%2010.9251%2011.1015L17.1126%2011.1004L14.4401%208.25671C12.2638%205.94105%2011.7593%205.39543%2011.7233%205.31849C11.5496%204.94687%2011.6313%204.49398%2011.9206%204.22486C12.1038%204.05445%2012.3793%203.96895%2012.612%204.0103Z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E');
                }

                &.MediaGalleryWidget__scrollButton--left {
                    @apply right-15.25 left-auto after:rotate-180 lg:right-13.25;
                }
                &.MediaGalleryWidget__scrollButton--right {
                    /* offset the negative margin of 5px */
                    @apply right-1.25;
                }
                &.MediaGalleryWidget__scrollButton--isDisabled {
                    @apply opacity-100;
                }
                .scrollButton__icon {
                    @apply hidden;
                }
            }
        }
        .MediaGalleryWidget__list {
            @apply pt-0 pb-8;
        }
    }
</style>
